import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { db, auth } from '../firebase.js';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar.js';
import './AdminDashboard.css';
import { collection, getDocs, doc, setDoc, deleteDoc, runTransaction, where, query, getDoc } from 'firebase/firestore'; 

// --- Reusable useCountUp Hook ---
const useCountUp = (end, duration = 1) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        let start = 0, frame = 0;
        const totalFrames = duration * 60;
        const counter = setInterval(() => {
            frame++;
            const progress = (frame / totalFrames) ** 2;
            const currentCount = Math.round(start + (end - start) * progress);
            setCount(currentCount);
            if (frame === totalFrames) { clearInterval(counter); setCount(end); }
        }, 1000 / 60);
        return () => clearInterval(counter);
    }, [end, duration]);
    return count;
};

// --- Icon Components ---
const IconPlus = () => ( <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"> <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line> </svg> );
const IconTrash = () => ( <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"> <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path> </svg> );
const IconCook = () => ( <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"> <path d="M20 10.5c0-.28-.03-.55-.08-.83a1 1 0 0 0-.42-.62l-2.06-1.37a2 2 0 0 1-1-1.7V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2a2 2 0 0 1-1 1.7L5.5 8.05a1 1 0 0 0-.42-.62c-.05.28-.08.55-.08.83V12a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1.5z"></path><path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"></path> </svg> );
const IconUsers = () => ( <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"> <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /> </svg> );
const IconPackage = () => ( <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"> <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /> </svg> );
const IconTruck = () => ( <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"> <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /> </svg> );
// --- End of Icon Components ---

// --- NEW: Stat Card Component ---
const StatCard = ({ title, value, icon, color }) => {
  const animatedValue = useCountUp(value, 0.8);
  return(
      <div className="stat-card">
          <div className={`stat-card-icon ${color}`}>
              {icon}
          </div>
          <p className="stat-card-value">{animatedValue}</p>
          <h3 className="stat-card-label">{title}</h3>
      </div>
  );
};

// --- UPDATED: DashboardStats Component ---
const DashboardStats = ({ subscribers, inventory, loading }) => {
  if (loading) return <p>Loading statistics...</p>;

  const totalMeals = subscribers.filter(s => (s.status || 'active') === 'active').length;
  const lowStockItems = inventory.filter(i => i.stock < i.threshold).length;

  return (
    <div>
      <h1 className="admin-header">Dashboard</h1>
      <div className="stats-grid">
        <StatCard 
          title="Total Active Subscribers" 
          value={subscribers.length} 
          icon={<IconUsers />} 
          color="blue" 
        />
        <StatCard 
          title="Meals to Deliver Today" 
          value={totalMeals} 
          icon={<IconTruck />} 
          color="emerald"
        />
        <StatCard 
          title="Items Low on Stock" 
          value={lowStockItems} 
          icon={<IconPackage />} 
          color="amber"
        />
      </div>
    </div>
  );
};

const SubscriberManager = ({ subscribers, loading, onEditSub }) => {
  if (loading) return <p>Loading subscribers...</p>;
  return (
    <div>
      <h1 className="admin-header">Subscribers</h1>
      <div className="table-container">
        <table className="subscriber-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map(user => (
              <tr key={user.id}>
                <td>{user.name}</td><td>{user.email}</td><td>{user.role}</td>
                <td><span className={`status-badge ${user.status || 'active'}`}>{user.status || 'Active'}</span></td>
                <td>
                  <button onClick={() => onEditSub(user)} className="action-button edit">
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InventoryManager = ({ inventory, loading, setInventory, setEditingItem }) => {
  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteDoc(doc(db, "inventory", itemId));
        setInventory(prev => prev.filter(item => item.id !== itemId));
      } catch (error) { console.error("Error deleting document: ", error); alert("Failed to delete item."); }
    }
  };
  if (loading) return <p>Loading inventory...</p>;
  return (
    <div>
      <div className="admin-header-row">
        <h1 className="admin-header">Inventory</h1>
        <button onClick={() => setEditingItem({})} className="add-button"><IconPlus /> Add New Item</button>
      </div>
      <div className="table-container">
        <table className="subscriber-table">
          <thead><tr><th>Ingredient</th><th>Stock</th><th>Unit</th><th>Per Meal</th><th>Threshold</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {inventory.map(item => {
              const isLow = item.stock < item.threshold;
              return (
                <tr key={item.id}>
                  <td>{item.name}</td><td>{item.stock}</td><td>{item.unit}</td><td>{item.qtyPerMeal || 0} {item.unit}</td><td>{item.threshold}</td>
                  <td><span className={`status-badge ${isLow ? 'paused' : 'active'}`}>{isLow ? 'Low' : 'OK'}</span></td>
                  <td>
                    <button onClick={() => setEditingItem(item)} className="action-button edit">Edit</button>
                    <button onClick={() => handleDelete(item.id)} className="action-button delete"><IconTrash /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminKitchenView = ({ subscribers, inventory, onCook }) => {
  const totalMeals = subscribers.filter(s => (s.status || 'active') === 'active').length;
  const dailyMenu = [{ name: "Bhindi", meals: totalMeals }, { name: "Roti", meals: totalMeals }];
  
  const requiredIngredients = dailyMenu.map(menuItem => {
    const invItem = inventory.find(i => i.name && i.name.toLowerCase() === menuItem.name.toLowerCase());
    if (!invItem || !invItem.qtyPerMeal) {
        return { name: menuItem.name, required: 0, inStock: invItem ? invItem.stock : 0, unit: invItem ? invItem.unit : '', isMissing: true, hasEnough: false, reason: invItem ? "Recipe (qty/meal) not set." : "Item not in inventory." };
    }
    const required = (invItem.qtyPerMeal || 0) * menuItem.meals;
    return { name: invItem.name, id: invItem.id, required: required, inStock: invItem.stock, unit: invItem.unit, isMissing: false, hasEnough: invItem.stock >= required };
  });
  
  const canCook = requiredIngredients.every(item => item.hasEnough && !item.isMissing);

  return (
    <div>
      <div className="admin-header-row">
        <h1 className="admin-header">Kitchen Prep for Today</h1>
        <div className="kitchen-stats"><span>Total Active Meals:</span><strong>{totalMeals}</strong></div>
      </div>
      <div className="table-container">
        <table className="subscriber-table">
          <thead><tr><th>Ingredient</th><th>Required</th><th>In Stock</th><th>Status</th></tr></thead>
          <tbody>
            {requiredIngredients.map(item => (
              <tr key={item.name} className={!item.hasEnough || item.isMissing ? 'row-danger' : ''}>
                <td>{item.name}</td><td>{item.required.toFixed(2)} {item.unit || ''}</td><td>{item.inStock} {item.unit || ''}</td>
                <td>
                  {item.isMissing ? (<span className="status-badge paused">{item.reason}</span>) : (<span className={`status-badge ${item.hasEnough ? 'active' : 'paused'}`}>{item.hasEnough ? 'OK' : 'Stock Low'}</span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => onCook(requiredIngredients, totalMeals)} disabled={!canCook || totalMeals === 0} className="add-button cook-button">
        <IconCook /> {canCook ? 'Cook Meals & Deduct Stock' : 'Cannot Cook (Check Stock/Recipe)'}
      </button>
    </div>
  );
};

const EditInventoryModal = ({ item, onClose, setInventory }) => {
  const [formData, setFormData] = useState(item);
  const [loading, setLoading] = useState(false);
  const isNew = !item.id;

  useEffect(() => { setFormData(item); }, [item]);
  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const docId = isNew ? formData.name.toLowerCase().replace(/\s+/g, '-') : item.id;
      const docRef = doc(db, "inventory", docId);
      const dataToSave = { 
        ...formData, 
        id: docId,
        name: formData.name, 
        stock: Number(formData.stock || 0), 
        threshold: Number(formData.threshold || 0),
        qtyPerMeal: Number(formData.qtyPerMeal || 0) 
      };
      await setDoc(docRef, dataToSave, { merge: true });
      if (isNew) {
        setInventory(prev => [dataToSave, ...prev]);
      } else {
        setInventory(prev => prev.map(i => i.id === docId ? dataToSave : i));
      }
      onClose();
    } catch (error) { console.error("Error saving document: ", error); alert("Failed to save item."); } 
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <h2 className="modal-title">{isNew ? 'Add New Item' : 'Edit Item'}</h2>
          <div className="modal-body">
            <div className="form-group">
              <label>Item Name</label>
              <input type="text" name="name" value={formData.name || ''} onChange={handleChange} required disabled={!isNew} />
              {!isNew && <small>Item Name cannot be changed (it's the ID).</small>}
            </div>
            <div className="form-row">
              <div className="form-group"><label>Current Stock</label><input type="number" step="0.01" name="stock" value={formData.stock || ''} onChange={handleChange} required /></div>
              <div className="form-group"><label>Unit</label><input type="text" name="unit" value={formData.unit || ''} onChange={handleChange} placeholder="e.g., kg, liters" /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Low Stock Threshold</label><input type="number" step="0.01" name="threshold" value={formData.threshold || ''} onChange={handleChange} required /></div>
              <div className="form-group"><label>Grams/Units per Meal</label><input type="number" step="0.001" name="qtyPerMeal" value={formData.qtyPerMeal || ''} onChange={handleChange} placeholder="e.g., 0.25" /></div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="button secondary">Cancel</button>
            <button type="submit" disabled={loading} className="button primary">
              {loading ? 'Saving...' : 'Save Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditSubscriptionModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      setLoading(true);
      const subDocRef = doc(db, "subscriptions", user.id);
      const subDoc = await getDoc(subDocRef);
      if (subDoc.exists()) {
        setFormData(subDoc.data());
      } else {
        setFormData({ 
          plan: 'Standard Plan', 
          mealsLeft: 20, 
          totalMeals: 20, 
          skippedMeals: 0, 
          deliveryStatus: 'Pending',
          deliveryETA: '8:00 PM'
        });
      }
      setLoading(false);
    };
    fetchSubscription();
  }, [user.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const docRef = doc(db, "subscriptions", user.id);
      const dataToSave = {
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        plan: formData.plan,
        mealsLeft: Number(formData.mealsLeft),
        totalMeals: Number(formData.totalMeals),
        skippedMeals: Number(formData.skippedMeals),
        deliveryStatus: formData.deliveryStatus,
        deliveryETA: formData.deliveryETA,
      };
      
      await setDoc(docRef, dataToSave, { merge: true });
      onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error("Error saving subscription: ", error);
      alert("Failed to save subscription.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <h2 className="modal-title">Manage Subscription for {user.name}</h2>
          {loading ? <p style={{padding: '2rem'}}>Loading...</p> : (
            <>
              <div className="modal-body">
                <div className="form-group">
                  <label>Plan Name</label>
                  <input type="text" name="plan" value={formData.plan || ''} onChange={handleChange} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Meals Left</label>
                    <input type="number" name="mealsLeft" value={formData.mealsLeft || ''} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Total Meals</label>
                    <input type="number" name="totalMeals" value={formData.totalMeals || ''} onChange={handleChange} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Delivery Status</label>
                    <select name="deliveryStatus" value={formData.deliveryStatus || 'Pending'} onChange={handleChange}>
                      <option>Pending</option>
                      <option>Out for Delivery</option>
                      <option>Delivered</option>
                      <option>On Hold</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Skipped Meals</label>
                    <input type="number" name="skippedMeals" value={formData.skippedMeals || 0} onChange={handleChange} required />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={onClose} className="button secondary">Cancel</button>
                <button type="submit" disabled={loading} className="button primary">
                  {loading ? 'Saving...' : 'Save Subscription'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};
// --- End of Content Components ---


function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [subscribers, setSubscribers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const fetchSubscribers = async () => {
    try {
      const q = query(collection(db, "users"), where("role", "==", "user"));
      const userSnapshot = await getDocs(q);
      return userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) { console.error("Error fetching subscribers: ", error); return []; }
  };
  const fetchInventory = async () => {
    try {
      const inventoryCollection = collection(db, "inventory");
      const invSnapshot = await getDocs(inventoryCollection);
      return invSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) { console.error("Error fetching inventory: ", error); return []; }
  };
  
  // --- UPDATED useEffect ---
  // This now fetches data for the dashboard tab as well
  useEffect(() => {
    setLoading(true);
    const loadData = async () => {
      if (activeTab === 'subscribers') {
        setSubscribers(await fetchSubscribers());
      } else if (activeTab === 'inventory') {
        setInventory(await fetchInventory());
      } else if (activeTab === 'kitchen' || activeTab === 'dashboard') {
        // Dashboard and Kitchen both need all data
        const [subData, invData] = await Promise.all([fetchSubscribers(), fetchInventory()]);
        setSubscribers(subData);
        setInventory(invData);
      }
      setLoading(false);
    };
    loadData();
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    navigate('/'); 
  };

  const handleCookAndDeductStock = async (requiredIngredients, totalMeals) => {
    if (totalMeals === 0) { alert("No active subscribers to cook for."); return; }
    if (!window.confirm(`This will deduct stock for ${totalMeals} meals. Are you sure?`)) { return; }
    setLoading(true);
    try {
      await runTransaction(db, async (transaction) => {
        for (const item of requiredIngredients) {
          if (item.isMissing || item.required === 0) continue;
          const itemRef = doc(db, "inventory", item.id);
          const itemDoc = await transaction.get(itemRef);
          if (!itemDoc.exists()) { throw new Error(`Item "${item.name}" not found.`); }
          const currentStock = itemDoc.data().stock;
          const newStock = currentStock - item.required;
          if (newStock < 0) { throw new Error(`Not enough stock for "${item.name}".`); }
          transaction.update(itemRef, { stock: newStock });
        }
      });
      alert("Stock successfully updated!");
      setInventory(await fetchInventory());
    } catch (error) {
      console.error("Transaction failed: ", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        // Pass the data to the stats component
        return <DashboardStats subscribers={subscribers} inventory={inventory} loading={loading} />;
      case 'subscribers':
        return <SubscriberManager subscribers={subscribers} loading={loading} onEditSub={setEditingSubscription} />;
      case 'inventory':
        return <InventoryManager inventory={inventory} loading={loading} setInventory={setInventory} setEditingItem={setEditingItem} />;
      case 'kitchen':
        return <AdminKitchenView subscribers={subscribers} inventory={inventory} onCook={handleCookAndDeductStock} />;
      default:
        return <DashboardStats subscribers={subscribers} inventory={inventory} loading={loading} />;
    }
  };

  return (
    <div className="admin-dashboard-layout">
      <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="admin-content-area">
        <div className="admin-top-bar">
          <span>Welcome, {currentUser?.name || currentUser?.email}!</span>
          <button onClick={handleLogout} className="simple-logout-button">
            Logout
          </button>
        </div>
        {renderContent()}
      </main>
      
      {editingItem && (
        <EditInventoryModal 
          item={editingItem} 
          onClose={() => setEditingItem(null)}
          setInventory={setInventory}
        />
      )}

      {editingSubscription && (
        <EditSubscriptionModal
          user={editingSubscription}
          onClose={() => setEditingSubscription(null)}
          onSave={() => {
            setEditingSubscription(null);
          }}
        />
      )}
    </div>
  );
}
export default AdminDashboard;

